import { redactSensitiveUrl } from "./clinicalProfileInviteContext";

const SENSITIVE_HEADER_KEYS = new Set(["authorization", "cookie", "set-cookie", "x-auth-token"]);

const scrubHeaders = (headers) => {
  if (!headers || typeof headers !== "object") return headers;
  const safe = {};
  for (const key of Object.keys(headers)) {
    safe[key] = SENSITIVE_HEADER_KEYS.has(key.toLowerCase()) ? "[redacted]" : headers[key];
  }
  return safe;
};

// Public process execution carries the same clinical PHI as authenticated
// /web calls, so both transport families share one telemetry boundary.
const isBackendApiUrl = (url) => typeof url === "string" && (url.includes("/web/") || url.includes("/api/v1/process-execute"));

const scrubRequestLike = (request) => {
  if (!request || typeof request !== "object") return request;
  const originalUrl = typeof request.url === "string" ? request.url : "";
  const safe = { ...request, url: redactSensitiveUrl(originalUrl) };
  if ("headers" in safe) safe.headers = scrubHeaders(safe.headers);
  if (isBackendApiUrl(originalUrl)) {
    if ("data" in safe) safe.data = "[redacted-phi]";
    if ("body" in safe) safe.body = "[redacted-phi]";
  }
  return safe;
};

export const scrubSentryBreadcrumb = (breadcrumb) => {
  if (!breadcrumb?.data) return breadcrumb;
  const isHttp = breadcrumb.category === "fetch" || breadcrumb.category === "xhr" || breadcrumb.type === "http";
  if (!isHttp) return breadcrumb;
  const originalUrl = typeof breadcrumb.data.url === "string" ? breadcrumb.data.url : "";
  const data = { ...breadcrumb.data, url: redactSensitiveUrl(originalUrl) };
  if (isBackendApiUrl(originalUrl)) {
    if ("data" in data) data.data = "[redacted-phi]";
    if ("body" in data) data.body = "[redacted-phi]";
    if ("response_body" in data) data.response_body = "[redacted-phi]";
  }
  return { ...breadcrumb, data };
};

export const sanitizeSentryEvent = (event) => {
  try {
    if (event.user && typeof event.user === "object") {
      // Allowlist, not a denylist: anything not named here is dropped. id is the
      // triage cohort key; organization is the tenant the event belongs to and is
      // not PHI (it's a practice name, never a patient identifier). email and every
      // other field the SDK or a caller attached is discarded.
      const { id, organization } = event.user;
      const safeUser = {};
      if (id) safeUser.id = id;
      if (organization) safeUser.organization = organization;
      event.user = safeUser;
    }
    if (event.request) event.request = scrubRequestLike(event.request);
    if (event.contexts?.response?.body) {
      event.contexts.response = { ...event.contexts.response, body: "[redacted-phi]" };
    }
    if (Array.isArray(event.breadcrumbs)) event.breadcrumbs = event.breadcrumbs.map(scrubSentryBreadcrumb);
    if (event.exception && Array.isArray(event.exception.values)) {
      event.exception.values = event.exception.values.map((value) => {
        if (value && typeof value.value === "string" && value.value.length > 2048) {
          return { ...value, value: value.value.slice(0, 2048) + "…[truncated]" };
        }
        return value;
      });
    }
    if (event.message && typeof event.message === "string" && event.message.length > 2048) {
      event.message = event.message.slice(0, 2048) + "…[truncated]";
    }
  } catch {
    // A privacy-filter failure must drop the event instead of exporting the
    // original bearer URL or PHI-bearing payload.
    return null;
  }
  return event;
};
